import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ACTIVE_CHILD_STORAGE_KEY } from "../components/Dashboard/shared/DashboardViewContext";

const ChildContext = createContext();

export const useChildContext = () => {
  const context = useContext(ChildContext);
  if (!context) {
    throw new Error("useChildContext must be used within a ChildProvider");
  }
  return context;
};

export const ChildProvider = ({ children }) => {
  const [currentChildId, setCurrentChildIdState] = useState(() => {
    if (typeof window === "undefined") {
      return null;
    }
    return window.localStorage.getItem(ACTIVE_CHILD_STORAGE_KEY) || null;
  });

  const applyCurrentChildId = useCallback((childId) => {
    const nextChildId = childId || null;
    setCurrentChildIdState((currentChildId) => (
      currentChildId === nextChildId ? currentChildId : nextChildId
    ));
  }, []);

  const setCurrentChildId = useCallback((childId) => {
    const nextChildId = childId || null;
    if (typeof window !== "undefined") {
      if (nextChildId) {
        window.localStorage.setItem(ACTIVE_CHILD_STORAGE_KEY, nextChildId);
      } else {
        window.localStorage.removeItem(ACTIVE_CHILD_STORAGE_KEY);
      }
      window.dispatchEvent(new CustomEvent("captureez:active-child-changed", {
        detail: { childId: nextChildId },
      }));
    }
    applyCurrentChildId(nextChildId);
  }, [applyCurrentChildId]);

  useEffect(() => {
    const handleActiveChildEvent = (event) => {
      applyCurrentChildId(event?.detail?.childId);
    };

    const handleStorage = (event) => {
      if (event.key === ACTIVE_CHILD_STORAGE_KEY) {
        applyCurrentChildId(event.newValue);
      }
    };

    window.addEventListener("captureez:set-active-child", handleActiveChildEvent);
    window.addEventListener("captureez:active-child-changed", handleActiveChildEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("captureez:set-active-child", handleActiveChildEvent);
      window.removeEventListener("captureez:active-child-changed", handleActiveChildEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, [applyCurrentChildId]);

  const value = useMemo(() => ({
    currentChildId,
    setCurrentChildId,
  }), [currentChildId, setCurrentChildId]);

  return (
    <ChildContext.Provider value={value}>{children}</ChildContext.Provider>
  );
};
